import React, { useState, useEffect } from "react";
import communityService from "../../services/communityService";
import {
  HiOutlineTrophy,
  HiOutlineStar,
  HiOutlineBookOpen,
  HiOutlineUsers,
  HiOutlineSparkles,
} from "react-icons/hi2";

/**
 * Onglet des classements
 * Affiche les meilleurs cuisiniers selon différents critères
 */
const LeaderboardTab = ({ user, token }) => {
  const [topUsers, setTopUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("score");

  useEffect(() => {
    loadLeaderboard();
  }, [sortBy]);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const data = await communityService.getTopUsers(20, sortBy);
      setTopUsers(data.users || []);
    } catch (error) {
      console.error("Erreur lors du chargement du classement:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRankDisplay = (index) => {
    if (index <= 2) return String(index + 1);
    return `#${index + 1}`;
  };

  const getRankColor = (index) => {
    if (index === 0) return "from-yellow-400 to-yellow-600";
    if (index === 1) return "from-gray-300 to-gray-500";
    if (index === 2) return "from-orange-400 to-orange-600";
    return "from-gray-200 to-gray-300";
  };

  const iconCls = "w-5 h-5 shrink-0";
  const sortOptions = [
    {
      value: "score",
      label: "Score Global",
      icon: <HiOutlineStar className={iconCls} />,
    },
    {
      value: "recipes",
      label: "Recettes",
      icon: <HiOutlineBookOpen className={iconCls} />,
    },
    {
      value: "followers",
      label: "Abonnés",
      icon: <HiOutlineUsers className={iconCls} />,
    },
    {
      value: "badges",
      label: "Badges",
      icon: <HiOutlineSparkles className={iconCls} />,
    },
  ];

  const getStatValue = (topUser) => {
    switch (sortBy) {
      case "recipes":
        return topUser.total_recipes || 0;
      case "followers":
        return topUser.total_followers || 0;
      case "badges":
        return topUser.total_badges || 0;
      default:
        return topUser.community_score || 0;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 sm:py-20">
        <div className="text-center px-4">
          <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-3 sm:mt-4 text-ink-700 dark:text-white text-sm sm:text-base">Chargement du classement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* En-tête et sélecteurs */}
      <div className="bg-white dark:bg-white/10 backdrop-blur-lg rounded-xl sm:rounded-2xl border border-ink-900/10 dark:border-white/20 p-4 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-bold text-ink-900 dark:text-white flex items-center gap-2 mb-4 sm:mb-6">
          <HiOutlineTrophy
            className="w-6 h-6 sm:w-7 sm:h-7 text-amber-400 shrink-0"
            aria-hidden
          />
          <span className="break-words">Classement des Meilleurs Cuisiniers</span>
        </h2>

        {/* Options de tri - 2x2 sur mobile, 4 colonnes sur desktop */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
          {sortOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setSortBy(option.value)}
              className={`flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-medium text-xs sm:text-base transition-all duration-200 touch-manipulation ${
                sortBy === option.value
                  ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md"
                  : "bg-ink-100 text-ink-700 border border-ink-900/10 hover:bg-ink-200 dark:bg-white/10 dark:text-white dark:border-white/20 dark:hover:bg-white/20"
              }`}
            >
              {option.icon}
              <span className="truncate">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Podium (Top 3) */}
      {topUsers.length >= 3 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
          {[1, 0, 2].map((position) => {
            const topUser = topUsers[position];
            if (!topUser) return null;

            return (
              <div
                key={topUser.id}
                className={`bg-gradient-to-br ${getRankColor(
                  position,
                )} rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 text-center ${
                  position === 0 ? "md:order-2 md:scale-105" : ""
                }`}
              >
                <div className="text-2xl sm:text-3xl font-bold text-white/90 mb-2 tabular-nums">
                  {getRankDisplay(position)}
                </div>

                <div className="w-14 h-14 sm:w-20 sm:h-20 mx-auto mb-3 sm:mb-4 bg-white rounded-xl sm:rounded-2xl flex items-center justify-center text-lg sm:text-2xl font-bold shadow-md">
                  {topUser.first_name?.[0] || topUser.username?.[0]}
                </div>

                <h3 className="text-base sm:text-lg font-bold text-white mb-1 truncate px-1">
                  {topUser.first_name && topUser.last_name
                    ? `${topUser.first_name} ${topUser.last_name}`
                    : topUser.username}
                </h3>

                <p className="text-white/80 text-xs sm:text-sm mb-2 sm:mb-3 truncate">
                  @{topUser.username}
                </p>

                <div className="bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl p-2 sm:p-3">
                  <div className="text-xl sm:text-3xl font-bold text-white">
                    {getStatValue(topUser)}
                  </div>
                  <div className="text-[10px] sm:text-xs text-white/80 font-medium mt-0.5 sm:mt-1 truncate">
                    {sortOptions.find((o) => o.value === sortBy)?.label}
                  </div>
                </div>

                <div className="mt-3 sm:mt-4 grid grid-cols-3 gap-1 sm:gap-2 text-white/80 text-[10px] sm:text-xs">
                  <div>
                    <div className="font-bold tabular-nums">{topUser.total_recipes}</div>
                    <div>Recettes</div>
                  </div>
                  <div>
                    <div className="font-bold tabular-nums">{topUser.total_followers}</div>
                    <div>Abonnés</div>
                  </div>
                  <div>
                    <div className="font-bold tabular-nums">{topUser.total_badges}</div>
                    <div>Badges</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Liste complète */}
      <div className="bg-white dark:bg-white/10 backdrop-blur-lg rounded-xl sm:rounded-2xl border border-ink-900/10 dark:border-white/20 overflow-hidden">
        <div className="divide-y divide-ink-900/10 dark:divide-white/10">
          {topUsers.map((topUser, index) => (
            <div
              key={topUser.id}
              className={`p-3 sm:p-4 hover:bg-ink-100 dark:hover:bg-white/10 transition-colors ${
                user && topUser.id === user.id ? "bg-amber-500/20" : ""
              }`}
            >
              <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                {/* Rang */}
                <div className="flex-shrink-0 w-8 sm:w-12 text-center">
                  <div className="text-sm sm:text-lg font-bold text-ink-700 dark:text-white/90 tabular-nums">
                    {getRankDisplay(index)}
                  </div>
                </div>

                {/* Avatar */}
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg sm:rounded-xl flex items-center justify-center text-white text-sm sm:text-base font-bold flex-shrink-0">
                  {topUser.first_name?.[0] || topUser.username?.[0]}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-ink-900 dark:text-white text-sm sm:text-base truncate">
                    {topUser.first_name && topUser.last_name
                      ? `${topUser.first_name} ${topUser.last_name}`
                      : topUser.username}
                    {user && topUser.id === user.id && (
                      <span className="ml-1 sm:ml-2 text-[10px] sm:text-xs text-amber-300 font-medium">
                        (Vous)
                      </span>
                    )}
                  </h4>
                  <p className="text-xs sm:text-sm text-ink-500 dark:text-white/80 truncate">@{topUser.username}</p>
                </div>

                {/* Statistiques - masquées sur très petit écran */}
                <div className="hidden sm:flex items-center gap-4 lg:gap-6 text-sm">
                  <div className="text-center">
                    <div className="font-bold text-ink-900 dark:text-white tabular-nums">{topUser.total_recipes}</div>
                    <div className="text-xs text-ink-500 dark:text-white/80">Recettes</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-ink-900 dark:text-white tabular-nums">{topUser.total_followers}</div>
                    <div className="text-xs text-ink-500 dark:text-white/80">Abonnés</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-ink-900 dark:text-white tabular-nums">{topUser.total_badges}</div>
                    <div className="text-xs text-ink-500 dark:text-white/80">Badges</div>
                  </div>
                </div>

                {/* Score principal - plus compact sur mobile */}
                <div className="flex-shrink-0">
                  <div className="px-2 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg sm:rounded-xl">
                    <div className="text-base sm:text-xl font-bold tabular-nums">
                      {getStatValue(topUser)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LeaderboardTab;
